import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Button, Input, Label, useToast } from '@shared/ui';
import {
  isPasswordPolicyMet,
  validateOtpCode,
  validatePasswordMatch,
  type PasswordPolicyRule,
} from '@entities/auth';
import { useResetPassword } from '@features/auth/reset-password';
import { useCooldown } from '@entities/auth';
import { passwordErrorMessage } from '@entities/auth';
import { useAuth } from '@entities/auth';
import { AuthScreen } from '@widgets/auth/auth-screen';
import { PasswordChangeWarning } from '@entities/auth';
import { PasswordPolicyChecklist } from '@entities/auth';

type ResetView = 'checking' | 'invalid' | 'form';

export function ResetPasswordPage() {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { token: routeToken = '' } = useParams<{ token?: string }>();
  const tokenRef = useRef(routeToken);
  const { checkToken, submitPassword, isChecking, isSubmitting } = useResetPassword();
  const { clearLocalSession } = useAuth();
  const { secondsLeft, start } = useCooldown();

  const [view, setView] = useState<ResetView>(routeToken ? 'checking' : 'invalid');
  const [requires2fa, setRequires2fa] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [policyRules, setPolicyRules] = useState<PasswordPolicyRule[]>([]);
  const startedRef = useRef(false);

  useLayoutEffect(() => {
    if (/^\/reset-password\/.+/.test(window.location.pathname)) {
      window.history.replaceState(window.history.state, '', '/reset-password');
    }
  }, []);

  useEffect(() => {
    document.title = t('recovery.resetTitle');
  }, [t]);

  useEffect(() => {
    const token = tokenRef.current;
    if (!token || startedRef.current) {
      return;
    }
    startedRef.current = true;

    void checkToken(token).then((result) => {
      if (!result.valid) {
        setView('invalid');
        return;
      }
      setRequires2fa(result.requires2fa);
      setView('form');
    });
  }, [checkToken]);

  const submit = async () => {
    const nextErrors: Record<string, string> = {};
    if (!isPasswordPolicyMet(password)) {
      nextErrors.password = t('recovery.policyLabel');
    }
    const match = validatePasswordMatch(password, repeatPassword);
    if (!match.isValid && match.error) {
      nextErrors.repeatPassword = t(match.error);
    }
    if (requires2fa && !validateOtpCode(code).isValid) {
      nextErrors.code = t('errors.codeInvalid');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const result = await submitPassword({
      token: tokenRef.current,
      password,
      code: requires2fa ? code : undefined,
    });

    if (result.success) {
      await clearLocalSession();
      navigate('/auth', { replace: true, state: { notice: 'passwordReset' } });
      return;
    }

    const failure = result.failure;
    if (!failure) {
      toast(t('recovery.requestFailed'), 'error');
      return;
    }
    if (failure.invalidLink) {
      setView('invalid');
      return;
    }
    if (failure.showOtp) {
      setRequires2fa(true);
    }
    if (failure.code === 'PASSWORD_POLICY') {
      setPolicyRules(failure.policyRules);
    }
    if (failure.code === 'TOO_MANY_REQUESTS') {
      start(failure.retryAfterSec ?? 60);
    }
    if (failure.code === 'OTP_INVALID') {
      setErrors({ code: t('recovery.otpInvalid') });
    }
    if (failure.code === 'PASSWORD_SAME') {
      setErrors({ password: t('recovery.samePassword') });
    }
    const message = passwordErrorMessage(failure, t, i18n.language);
    if (message && failure.code !== 'OTP_INVALID' && failure.code !== 'PASSWORD_SAME') {
      toast(message, 'error');
    }
  };

  return (
    <AuthScreen>
      <h1 className="auth-panel-title w-full text-center lg:text-left">
        {t('recovery.resetTitle')}
      </h1>

        {(view === 'checking' || isChecking) && (
          <p className="w-full text-sm text-muted-foreground">{t('recovery.checkingLink')}</p>
        )}

        {view === 'invalid' && !isChecking && (
          <div className="w-full space-y-4">
            <p className="text-sm text-destructive">{t('recovery.invalidLink')}</p>
            <Button className="w-full" variant="default" onClick={() => navigate('/forgot-password')}>
              {t('recovery.requestNewLink')}
            </Button>
          </div>
        )}

        {view === 'form' && (
          <form
            noValidate
            className="w-full space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <div className="relative space-y-2">
              <Label htmlFor="reset-password">{t('recovery.newPassword')}</Label>
              <Input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                disabled={isSubmitting || secondsLeft > 0}
                error={errors.password}
                icon={<Lock className="h-4 w-4" />}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPolicyRules([]);
                }}
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-muted-foreground"
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <PasswordPolicyChecklist password={password} failedRules={policyRules} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-repeat">{t('recovery.repeatPassword')}</Label>
              <Input
                id="reset-repeat"
                type="password"
                autoComplete="new-password"
                value={repeatPassword}
                disabled={isSubmitting || secondsLeft > 0}
                error={errors.repeatPassword}
                icon={<Lock className="h-4 w-4" />}
                onChange={(event) => setRepeatPassword(event.target.value)}
              />
            </div>

            {requires2fa && (
              <div className="space-y-2">
                <Label htmlFor="reset-code">{t('recovery.otpCode')}</Label>
                <Input
                  id="reset-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  disabled={isSubmitting || secondsLeft > 0}
                  error={errors.code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
            )}

            <PasswordChangeWarning />

            <Button
              type="submit"
              className="w-full"
              variant="default"
              loading={isSubmitting}
              disabled={secondsLeft > 0}
            >
              {secondsLeft > 0
                ? t('recovery.lockedFor', { seconds: secondsLeft })
                : t('recovery.submitReset')}
            </Button>
          </form>
        )}
    </AuthScreen>
  );
}
